import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';

import { UnsubscribeOnDestroyAdapter } from '@shared';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { environment } from '@enviroment/environment';
import { Branch } from '@core/models/Branch';
import { GeneralFunctionsService } from '@core/service/generalFunctions.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { MsgResponse } from '@core/models/MsgResponse';
import { AuthManagementService } from '@core/service/auth-management.service';

@Injectable({
  providedIn: 'root',
})
export class BranchService extends UnsubscribeOnDestroyAdapter {
  isTblLoading = true;
  dataChange: BehaviorSubject<Branch[]> = new BehaviorSubject<Branch[]>([]);

  constructor(
    private httpClient: HttpClient,
    private gfs: GeneralFunctionsService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService
  ) {
    super();
  }

  get data(): Branch[] {
    return this.dataChange.value;
  }

  getAllBranches(idGEMP?: string, soloActivas?: boolean): void {
    this.isTblLoading = true;

    // ✅ SI ES PARTICULAR, USA EL NUEVO ENDPOINT
    if (this.authManagement.isUserParticular()) {
      this.subs.sink = this.httpClient
        .get<DefaultResponse<Branch[]>>(
          `${environment.backend_url}Consulta/SucursalesPorUsuario`
        )
        .subscribe({
          next: (data) => {
            this.isTblLoading = false;
            this.dataChange.next(this.normalizeBranches(data.data ?? []));
          },
          error: (err: HttpErrorResponse) => {
            this.isTblLoading = false;
            this.dataChange.next([]);
            this.httpMsgHandler.handleMsg(err);
          },
        });

      return;
    }

    // ✅ FLUJO NORMAL PARA LOS DEMAS ROLES
    let params = new HttpParams();

    if (idGEMP) {
      params = params.set('IdGEMP', idGEMP);
    }

    // Solo agrega SoloActivas si realmente viene
    if (soloActivas !== undefined) {
      params = params.set('SoloActivas', String(soloActivas));
    }

    this.subs.sink = this.httpClient
      .get<DefaultResponse<Branch[]>>(
        `${environment.backend_url}Sucursales/GetSucursales`,
        { params }
      )
      .subscribe({
        next: (data) => {
          this.isTblLoading = false;
          this.dataChange.next(this.normalizeBranches(data.data ?? []));
        },
        error: (err: HttpErrorResponse) => {
          this.isTblLoading = false;
          this.dataChange.next([]);
          this.httpMsgHandler.handleMsg(err);
        },
      });
  }

  createUpdateBranch(data: any) {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Sucursales/UpsertSucursal`;

    return this.httpClient.post<DefaultResponse<any>>(url, cleanData);
  }

  deleteBranch(id: string): void {
    this.httpClient.delete(`${environment.backend_url}Sucursales/${id}`)
      .subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res as MsgResponse);
        },
        error: (err: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(err);
        },
      });
  }

  getByIdSucursal(idSucursal: string) {
    const params = new HttpParams().set('IdSucursal', idSucursal);

    return this.httpClient
      .get<DefaultResponse<Branch[]>>(
        `${environment.backend_url}Sucursales/GetSucursales`,
        { params }
      )
      .pipe(
        map((res): DefaultResponse<Branch> => ({
          ...res,
          data: res.data && res.data.length > 0
            ? this.normalizeBranches([res.data[0]])[0]
            : {} as Branch
        }))
      );
  }

  private normalizeBranches(branches: Branch[]): Branch[] {
    return branches.map((value: Branch) => {
      const newBranch = {
        ...value,
        calle1: '',
        calle2: '',
        calle3: ''
      };

      if (newBranch.domicilio) {
        const direccion = newBranch.domicilio
          .split(',')
          .map((part) => part.trim());

        newBranch.calle1 = direccion[0] || '';
        newBranch.calle2 = direccion.slice(1, direccion.length - 1).join(', ') || '';
        newBranch.calle3 = direccion[direccion.length - 1] || '';
      }

      return newBranch;
    });
  }

  clearBranches(): void {
    this.dataChange.next([]);
  }
}
